/**
 * 資料エディタ — セクション編集 Sheet。右からスライドイン。
 * registry 駆動で variant 切替 + フィールド編集。blur で永続化。
 */
import type { DocumentSection } from '@goenhub/shared';
import { Pencil, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
  useUpsertDocument,
  type DocumentRecord,
} from '@/hooks/useDocuments';

import { getSection, getVariant, variantSample } from '../sectionRegistry';
import { DocumentSectionOptimizeDialog } from './DocumentSectionOptimizeDialog';

interface Props {
  document: DocumentRecord;
  sectionId: string;
  onClose: () => void;
}

export function DocumentSectionEditorSheet({
  document,
  sectionId,
  onClose,
}: Props) {
  const section = document.sections.find((s) => s.id === sectionId);
  const upsert = useUpsertDocument();
  const [config, setConfig] = useState<Record<string, unknown>>(
    section?.config ?? {},
  );
  const [variant, setVariant] = useState<string | undefined>(section?.variant);
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const lastSavedRef = useRef<string>(
    JSON.stringify({ variant: section?.variant, config: section?.config ?? {} }),
  );

  // commit 関数の closure 問題を回避するため、最新 config を ref で保持する。
  // onChange ハンドラは setConfig (非同期) と同時に ref を同期更新し、
  // 直後に呼ばれる onCommit が常に最新値を見られるようにする。
  const configRef = useRef<Record<string, unknown>>(section?.config ?? {});
  // backdrop ドラッグ判定用 (テキスト選択時の誤クローズ防止)
  const downOnBackdropRef = useRef(false);
  const variantRef = useRef<string | undefined>(section?.variant);

  useEffect(() => {
    if (!section) return;
    const serialized = JSON.stringify({
      variant: section.variant,
      config: section.config ?? {},
    });
    // 既に保存済みデータと一致（自分の save 起因の refetch）→ state 同期不要。
    // 入力中の IME 競合・カーソル末尾飛びを防ぐため、内容が変わらない限り
    // input value prop を再生成しない。
    if (serialized === lastSavedRef.current) return;
    setConfig(section.config ?? {});
    setVariant(section.variant);
    configRef.current = section.config ?? {};
    variantRef.current = section.variant;
    lastSavedRef.current = serialized;
  }, [sectionId, section]);

  const persist = async (
    nextVariant: string | undefined,
    nextConfig: Record<string, unknown>,
  ) => {
    const serialized = JSON.stringify({
      variant: nextVariant,
      config: nextConfig,
    });
    if (serialized === lastSavedRef.current) return;
    const updated: DocumentSection[] = document.sections.map((s) =>
      s.id === sectionId
        ? { ...s, variant: nextVariant, config: nextConfig }
        : s,
    );
    try {
      await upsert.mutateAsync({
        id: document.id,
        title: document.title,
        slug: document.slug,
        status: document.status,
        sections: updated,
        autoReplyTemplate: document.autoReplyTemplate ?? null,
      });
      lastSavedRef.current = serialized;
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // ref を同期更新する onChange ラッパ。これにより、同一 tick 内で
  // onChange → onCommit と続けて呼ばれても commit が最新 config を見られる。
  const handleConfigChange = (next: Record<string, unknown>) => {
    configRef.current = next;
    setConfig(next);
  };

  const commit = () => {
    void persist(variantRef.current, configRef.current);
  };

  // 任意の config 変更を 500ms 後に自動保存する safety net。
  // ArrayField の add/remove・ギャラリー画像ピック等、Editor 側で
  // 明示的に onCommit を呼ばないケースをカバーするため。
  // persist 内で serialized === lastSavedRef のガードがあるので
  // 重複保存にはならない。
  useEffect(() => {
    if (!section) return;
    const handle = setTimeout(() => {
      void persist(variantRef.current, configRef.current);
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // フックはここまでに全て呼ぶこと。早期 return の後にフックを置くと
  // レンダー間でフック数が変わり React #310 でクラッシュする (2026-08-24 実害)。
  if (!section) return null;
  const def = getSection(section.type);
  if (!def) return null;
  const resolved = getVariant(section.type, variant);
  const activeVariantKey = resolved?.key ?? def.defaultVariant;
  const activeVariant = resolved?.variant;

  const handleVariantChange = (nextKey: string) => {
    if (nextKey === activeVariantKey) return;
    if (
      !confirm(
        `バリアントを「${def.variants[nextKey]?.label ?? nextKey}」に切り替えると、現在の入力内容は新しいレイアウトのサンプルに置き換わります。続行しますか？`,
      )
    )
      return;
    const sample = variantSample(section.type, nextKey);
    variantRef.current = nextKey;
    configRef.current = sample;
    setVariant(nextKey);
    setConfig(sample);
    void persist(nextKey, sample);
  };

  const Editor = activeVariant?.Editor;

  return (
    <div
      className="fixed inset-0 bg-black/30 z-40 flex justify-end"
      onMouseDown={(e) => {
        // mousedown 起点が backdrop 自身であるかを記録
        // (textドラッグ選択時、内部から外に上げた mouseup を「外側クリック」と誤判定しないため)
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (!downOnBackdropRef.current) return;
        onClose();
      }}
    >
      <div
        className="w-[480px] h-full bg-[var(--bg-elevated)] border-l border-[var(--border)] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-elevated)] z-10">
          <div className="flex items-center gap-2">
            <Pencil className="size-4 text-[var(--text-muted)]" />
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-[var(--text-muted)]">
                EDIT · {def.label}
              </div>
              <div className="text-sm font-medium truncate max-w-[300px]">
                {def.label} ({activeVariant?.label ?? activeVariantKey})
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4 text-xs">
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={() => setOptimizeOpen(true)}
          >
            <Sparkles className="size-3.5 mr-1.5 text-[var(--accent)]" />
            AI で最適化
          </Button>

          {Object.keys(def.variants).length > 1 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold text-[var(--text-muted)]">
                デザインバリアント
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(def.variants).map(([key, v]) => {
                  const isActive = key === activeVariantKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleVariantChange(key)}
                      className={`text-left p-2 rounded border text-[11px] transition-colors ${
                        isActive
                          ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                          : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      <div className="font-semibold">{v.label}</div>
                      {v.description && (
                        <div className="text-[10px] text-[var(--text-muted)] mt-0.5 line-clamp-2">
                          {v.description}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {Editor && (
            <Editor
              config={config}
              onChange={handleConfigChange}
              onCommit={commit}
            />
          )}
        </div>
      </div>

      <DocumentSectionOptimizeDialog
        open={optimizeOpen}
        onOpenChange={setOptimizeOpen}
        documentId={document.id}
        sectionId={sectionId}
        sectionLabel={`${def.label} (${activeVariant?.label ?? activeVariantKey})`}
        onOptimized={(nextConfig) => {
          // サーバ側で Firestore 更新済み。ローカル state も即時反映。
          configRef.current = nextConfig;
          setConfig(nextConfig);
          lastSavedRef.current = JSON.stringify({
            variant: variantRef.current,
            config: nextConfig,
          });
        }}
      />
    </div>
  );
}
